import { Inject, Injectable } from '@angular/core';
import { ENVIRONMENT_CONFIG, EnvironmentConfiguration } from 'src/environments/models/environment.model';
import { ApiService } from '../constants/api-services.constant';

/**
 * Central builder for backend API URLs.
 *
 * Combines the configured API endpoint with a (case-sensitive) service segment
 * and optional path segments, so that service names are defined once (see
 * {@link ApiService}) and URLs are assembled consistently everywhere.
 */
@Injectable({ providedIn: 'root' })
export class ApiUrlService {
  /**
   * The configured API base URL (the environment endpoint), e.g. `https://host/api/`.
   * Exposed so consumers such as the HTTP interceptor can identify API requests
   * without reaching into the environment configuration directly.
   */
  readonly baseUrl: string;
  private readonly trimmedBase: string;

  constructor(@Inject(ENVIRONMENT_CONFIG) environmentConfig: EnvironmentConfiguration) {
    this.baseUrl = environmentConfig.endpoint;
    this.trimmedBase = this.baseUrl.replace(/\/+$/g, '');
  }

  /**
   * Builds a full URL for the given service and optional path segments.
   *
   * Duplicate slashes between the endpoint and the segments are collapsed, so
   * the result is correct whether or not the configured endpoint ends with `/`.
   *
   * @example
   * build(ApiService.ProjectProductManagement, 'products');
   * // => `${endpoint}ProjectProductManagement/products`
   */
  build(service: ApiService, ...segments: (string | number)[]): string {
    let path = service;
    for (const segment of segments) {
      const trimmed = `${segment}`.replace(/^\/+|\/+$/g, '');
      if (trimmed.length > 0) {
        path += `/${trimmed}`;
      }
    }

    return `${this.trimmedBase}/${path}`;
  }
}
